import { PageContainer } from "components/core/basic/PageContainer";
import { Switch } from "components/fields/base/Switch";
import { useFetch } from "hooks/api";
import useDefaultState from "hooks/useDefaultState";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "redux/hooks";
import api from "utils/api";
import { css } from "../../../styled-system/css";
import { Container } from "react-bootstrap";
import Tabs from "components/core/basic/Tabs";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { Button } from "components/core/basic/Button";
import { buildDateColumn } from "views/merchandise/columns/buildDateColumn";
import CenteredModal from "components/core/basic/CenteredModal";
import { HStack, VStack } from "../../../styled-system/jsx";
import useQueryParams from "hooks/useQueryParams";
import Papa from "papaparse";
import SelectField from "components/core/basic/SelectField";
import { TextField } from "components/fields/base/TextField";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";

const CreateDataLinkCustomUploadModal = ({ show, setShow, amcInstances }) => {
  const inputRef = useRef();
  const queryClient = useQueryClient();
  const [selectAMCInstance, setSelectAMCInstance] = useDefaultState(amcInstances?.[0]?.id);
  const [name, setName] = useState("");
  const [uplodatedData, setUploadedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const createDataLink = useMutation({
    mutationFn: (body) => {
      return api.post("/amc/datalink/upload/", {
        ...body,
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datalinks"],
      });
      setShow(false);
    },
  });
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setErrors([]);
        const text = e.target.result;
        const { data, errors } = Papa.parse(text, {
          header: true,
          quoteChar: '"',
          escapeChar: "\\",
        });
        setErrors(errors);
        if (errors.length > 0) {
          inputRef.current.value = "";
          return;
        }
        setUploadedData(data);
      };
      reader.readAsText(file);
    }
  };
  return (
    <CenteredModal
      show={show}
      closeModal={() => setShow(false)}
      title="Upload Custom Data"
      dialogClassName="modal-60w"
      content={
        <VStack gap={16}>
          <p className="text-center">
            Upload your custom data to import 1st Party Data into AMC. The file should be in CSV format.
          </p>
          <VStack gap="4">
            <HStack gap="16">
              {amcInstances?.length > 1 && (
                <SelectField
                  label="AMC Instance"
                  value={selectAMCInstance}
                  onChange={(e) => {
                    setSelectAMCInstance(e.target.value);
                  }}
                  options={amcInstances.map((instance) => {
                    return {
                      label: instance?.raw?.instanceName,
                      value: instance.id,
                    };
                  })}
                />
              )}
              <TextField
                className={css({
                  flexDirection: "row",
                  alignItems: "center",
                })}
                label="Name"
                value={name}
                onChange={(val) => setName(val)}
              />
              <input type="file" ref={inputRef} accept={"text/csv"} onChange={handleUpload} />
            </HStack>
            {errors.length > 0 && <p className="text-danger">Errors in the file</p>}
          </VStack>
          <HStack justifyContent="space-between" width="full">
            <a target="_blank" href="/first_party_data_template.csv">
              Download Template
            </a>
            <HStack justifyContent="end" gap="2">
              <Button
                variant="secondary"
                onPress={() => {
                  inputRef.current.value = "";
                  setErrors([]);
                  setUploadedData(null);
                  setShow(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onPress={() =>
                  createDataLink.mutate({
                    marketplace: "US",
                    data: uplodatedData,
                    amc_instance: selectAMCInstance,
                    name,
                  })
                }
                isPending={createDataLink.isPending}
                isDisabled={!uplodatedData || errors?.length > 0}
              >
                Continue
              </Button>
            </HStack>
          </HStack>
        </VStack>
      }
    />
  );
};

const DataLinkCustomUploadTable = ({ dataLinks, isLoading, amcInstances }) => {
  const [show, setShow] = useState(false);
  const tableRef = useRef();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableData = useMemo(() => {
    return dataLinks.map((dataLink) => {
      return {
        ...dataLink,
        amcInstanceName: dataLink.amc_instance?.raw?.instanceName,
        uploadStatus: dataLink.upload_status?.[0]?.upload_status,
      };
    });
  }, [dataLinks]);
  const columns = [
    buildTextColumn("amcInstanceName", "AMC Instance"),
    buildTextColumn("uploadStatus", "Status"),
    buildDateColumn("created_on", "Created On"),
  ];

  return (
    <>
      <CreateDataLinkCustomUploadModal show={show} setShow={setShow} amcInstances={amcInstances} />
      <BulkActionsTable
        noDataText={`Upload your custom data to import 1st Party Data into AMC.`}
        marketPlace={marketPlace}
        tableRef={tableRef}
        tableId={`amc-custom-upload-table`}
        titleCol={{
          id: "name",
          isStatic: true,
          Header: "Upload",
          accessor: "name",
          style: { width: "150px" },
          width: 150,
          Cell: ({ value }) => {
            return value;
          },
          filterMethod: () => {
            return;
          },
          Filter: () => null,
        }}
        columnSpecs={columns}
        data={tableData}
        hideFilterToggleIcon={true}
        isExpandable={false}
        selectionKey={"id"}
        defaultSorted={[{ id: "name", desc: false }]}
        getSearchCriteria={(row) => {
          const { name } = row;
          return `${name}`;
        }}
        tableRowDescription={"Custom Uploads"}
        minRows={5}
        isLoading={isLoading}
        leftActionButton={
          <Button
            onPress={() => {
              setShow(true);
            }}
          >
            Upload
          </Button>
        }
      />
    </>
  );
};

const DataLinkConfiguration = ({ dataLinkMap, instance, org }) => {
  const queryClient = useQueryClient();
  const createDataLink = useMutation({
    mutationFn: (body) => {
      return api.post("/amc/datalink/", {
        ...body,
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datalinks"],
      });
    },
  });
  const updateDataLink = useMutation({
    mutationFn: (dataLink) => {
      return api.patch(`/amc/datalink/${dataLink.id}/`, dataLink);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datalinks"],
      });
    },
  });
  const dataLink = dataLinkMap[`${org.id}-${instance.id}`];
  const [enabled, setEnabled] = useDefaultState(dataLink?.enabled || false);

  const handleSwitchChange = (orgId, amcInstanceId, enabled) => {
    setEnabled(enabled);
    const dataLink = dataLinkMap[`${orgId}-${amcInstanceId}`];
    if (dataLink) {
      updateDataLink.mutate({
        id: dataLink.id,
        enabled,
      });
    } else {
      createDataLink.mutate({ shopify_org: orgId, amc_instance: amcInstanceId, enabled, marketplace: "US" });
    }
  };
  const status = dataLink?.upload_status?.[0]?.upload_status;
  return (
    <div>
      <p className={css({ marginBottom: "2rem" })}>
        Sync your Shopify customer data into AMC so you can create custom audiences based on your shopify customers.
      </p>
      <Switch
        isSelected={enabled}
        onChange={(e) => {
          handleSwitchChange(org.id, instance.id, e);
        }}
      >
        Enabled AMC 1st Party Data
        {enabled && status && status !== "SUCCESS" && (
          <span
            title={status}
            className={css({
              marginLeft: "10px",
              backgroundColor: status === "FAILED" ? "red" : "green",
            })}
          >
            {status}
          </span>
        )}
      </Switch>
    </div>
  );
};

const AMCConfiguration = () => {
  const [tab, setTab] = useQueryParams("tab", "shopify");
  const { organization_id: orgId } = useAppSelector((state) => state.user);
  const { data: dataLinks, isLoading } = useFetch(
    ["datalinks", orgId],
    "/amc/datalink/",
    {},
    {
      select: (res) => res.data.results,
    },
  );
  const { data: orgs } = useFetch(
    ["orgs", orgId],
    "/api/organizations",
    {
      org_type: "shopify",
    },
    {
      select: (res) => res.data,
    },
  );
  const { data: amcInstances } = useFetch(
    ["amcInstances", orgId],
    "/amc/instances/",
    {},
    {
      select: (res) => res.data,
    },
  );

  const dataLinkMap = useMemo(() => {
    return dataLinks?.reduce((acc, dataLink) => {
      const key = `${dataLink.shopify_org?.id}-${dataLink.amc_instance?.id}`;
      acc[key] = dataLink;
      return acc;
    }, {});
  }, [dataLinks]);

  const customUploads = useMemo(() => {
    return dataLinks?.filter((dataLink) => dataLink.data_source === "USER_UPLOAD") || [];
  }, [dataLinks]);

  return (
    <PageContainer>
      <Container>
        <h1
          className={css({
            fontSize: "24px",
            fontWeight: "600",
            textAlign: "center",
            marginBottom: "20px",
          })}
        >
          AMC Configuration
        </h1>
        <h3
          className={`fs-2 ${css({
            fontWeight: "600",
            marginBottom: "20px",
          })}`}
        >
          First Party Data Connections
        </h3>
        <Tabs
          selectedTab={tab}
          onTabChange={setTab}
          tabs={[
            { label: "Shopify", key: "shopify" },
            { label: "Custom Upload", key: "custom" },
          ]}
        />
        {tab === "shopify" && (
          <>
            {orgs?.length === 0 && (
              <div
                className={css({
                  padding: "15px",
                  lineHeight: "150px",
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  color: "gray.500",
                  background: "gray.100",
                  borderRadius: "15px",
                  textAlign: "center",
                  height: "150px",
                })}
              >
                <span>No Shopify organizations found.</span>
              </div>
            )}
            {orgs?.map((org) => {
              return (
                <div
                  className={`card fs-stardard ${css({ paddingTop: "15px", paddingBottom: "15px", paddingLeft: "20px", paddingRight: "20px" })}`}
                  key={org.id}
                >
                  <h5 className={css({ fontWeight: "bold" })}>{org.profile_name}</h5>
                  {amcInstances &&
                    amcInstances.map((instance) => {
                      return (
                        <DataLinkConfiguration
                          key={instance.id}
                          dataLinkMap={dataLinkMap}
                          instance={instance}
                          org={org}
                        />
                      );
                    })}
                </div>
              );
            })}
          </>
        )}
        {tab === "custom" && (
          <div
            className={`card fs-stardard ${css({ paddingTop: "15px", paddingBottom: "15px", paddingLeft: "20px", paddingRight: "20px" })}`}
          >
            <DataLinkCustomUploadTable amcInstances={amcInstances} dataLinks={customUploads} isLoading={isLoading} />
          </div>
        )}
      </Container>
    </PageContainer>
  );
};

export default AMCConfiguration;
